import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { QueryBox, SAMPLE_QUERIES } from "@/components/QueryBox";
import { ResultColumns } from "@/components/ResultColumns";
import { type Verdict, VoteBar } from "@/components/VoteBar";
import { type Hit, type Mode, type SearchResponse, postSearch, postVote } from "@/lib/api";
import { canonicalKey } from "@/lib/canonical";
import { useProfileLabel } from "@/lib/profileLabels";
import { useSelection } from "@/lib/selection";

type Phase = "empty" | "searching" | "results" | "submitted" | "error";

interface ResultsState {
  queryId: string;
  hitsLeft: Hit[];
  hitsRight: Hit[];
  latencyLeftMs: number;
  latencyRightMs: number;
}

/**
 * The heart of the arena. Phases, read top to bottom in natural order:
 *   - empty: neither the no-pair blocker, nor a partial-pair hint, nor a
 *     results view. Query box + seed queries.
 *   - searching: query in flight, skeleton columns shimmer.
 *   - results: two blinded columns + the vote bar. Keyboard 1/2/3/4 casts.
 *   - submitted: vote recorded, 1200 ms confirmation flash, then → empty.
 *   - error: search or vote failed. Inline banner + retry.
 *
 * The no-pair view is rendered *before* phase is consulted — it short-
 * circuits when either slot is null. A distinct partial-pair view
 * acknowledges the filled slot so the user knows why they aren't seeing
 * results.
 */
export function Search() {
  const { mode, profileA, profileB } = useSelection();
  const hasPair = !!(profileA && profileB);
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>("empty");
  const [query, setQuery] = useState("");
  const [lastQuery, setLastQuery] = useState("");
  const [state, setState] = useState<ResultsState | null>(null);
  const [verdict, setVerdict] = useState<Verdict | null>(null);

  // Any mode/slot change: clear everything. Stale hits belong to a different
  // ranking profile.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the TRIGGER for the reset — the body only calls setters (stable refs), so biome can't see that we want this effect to re-run on pair change
  useEffect(() => {
    setPhase("empty");
    setQuery("");
    setLastQuery("");
    setState(null);
    setVerdict(null);
  }, [mode, profileA, profileB]);

  const searchMut = useMutation({
    mutationFn: async (q: string): Promise<SearchResponse> => {
      if (!profileA || !profileB) throw new Error("no_pair");
      return postSearch({ mode, profileA, profileB, query: q });
    },
    onSuccess: (data) => {
      setState({
        queryId: data.queryId,
        hitsLeft: data.hitsLeft,
        hitsRight: data.hitsRight,
        latencyLeftMs: data.latencyLeftMs,
        latencyRightMs: data.latencyRightMs,
      });
      setPhase("results");
    },
    onError: () => setPhase("error"),
  });

  const voteMut = useMutation({
    mutationFn: ({ queryId, v }: { queryId: string; v: Verdict }) =>
      postVote({ queryId, verdict: v }),
    onSuccess: () => {
      // Invalidate the Results cache for the current canonical pair so a
      // parallel Results tab (or the user's own navigation after voting)
      // reflects the new vote without waiting out the 30s staleTime.
      const key = canonicalKey(mode, profileA, profileB);
      if (key) {
        queryClient.invalidateQueries({ queryKey: ["results", key] });
      }
      setPhase("submitted");
      setTimeout(() => {
        setPhase("empty");
        setQuery("");
        setLastQuery("");
        setState(null);
        setVerdict(null);
      }, 1200);
    },
    onError: () => setPhase("error"),
  });

  const doSearch = (q: string) => {
    if (!q.trim() || !hasPair) return;
    setLastQuery(q);
    setState(null);
    setVerdict(null);
    setPhase("searching");
    searchMut.mutate(q);
  };

  const castVote = (v: Verdict) => {
    if (!state) return;
    setVerdict(v);
    voteMut.mutate({ queryId: state.queryId, v });
  };

  // Keyboard 1/2/3/4 → verdict. `e.code` rather than `e.key` so AZERTY and
  // QWERTZ users can vote without Shift or layout-remapping. Only active in
  // the `results` phase so hotkeys don't fire mid-search or during the
  // 1200 ms success flash.
  useEffect(() => {
    if (phase !== "results") return;
    const map: Record<string, Verdict> = {
      Digit1: "left",
      Digit2: "both_good",
      Digit3: "both_bad",
      Digit4: "right",
      Numpad1: "left",
      Numpad2: "both_good",
      Numpad3: "both_bad",
      Numpad4: "right",
    };
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      // "A" skipped so digit keys don't cast a vote while a result-card
      // anchor has keyboard focus (Tab-into-card + 1 would otherwise fire
      // a "left" vote).
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "A") return;
      const v = map[e.code];
      if (v) {
        e.preventDefault();
        castVote(v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // biome-ignore lint/correctness/useExhaustiveDependencies: castVote closes over state but re-registering on every state tick is fine for a keyboard handler and avoids a ref dance
  }, [phase, castVote]);

  // Partial-pair: one set, one null.
  if (!hasPair) {
    if (profileA && !profileB) {
      return <PartialPairEmpty mode={mode} set="A" apiName={profileA} />;
    }
    if (!profileA && profileB) {
      return <PartialPairEmpty mode={mode} set="B" apiName={profileB} />;
    }
    return <NoPairEmpty mode={mode} />;
  }

  if (phase === "empty") {
    return (
      <EmptyWithPair
        mode={mode}
        profileA={profileA!}
        profileB={profileB!}
        query={query}
        setQuery={setQuery}
        doSearch={doSearch}
      />
    );
  }

  return (
    <ResultsView
      mode={mode}
      profileA={profileA!}
      profileB={profileB!}
      query={query}
      lastQuery={lastQuery}
      setQuery={setQuery}
      doSearch={doSearch}
      phase={phase}
      state={state}
      verdict={verdict}
      castVote={castVote}
    />
  );
}

// -----------------------------------------------------------------------------
// Empty states
// -----------------------------------------------------------------------------

function NoPairEmpty({ mode }: { mode: Mode }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 20,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          border: "1px dashed var(--border)",
          display: "grid",
          placeItems: "center",
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        A · B
      </div>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}
      >
        Pick two profiles to start
      </h1>
      <p
        style={{
          margin: 0,
          color: "var(--muted-foreground)",
          fontSize: 13,
          maxWidth: 440,
          textAlign: "center",
          lineHeight: 1.55,
        }}
      >
        Use the sidebar to select{" "}
        <b style={{ color: "var(--foreground)", fontWeight: 500 }}>Profile A</b> and{" "}
        <b style={{ color: "var(--foreground)", fontWeight: 500 }}>Profile B</b> in{" "}
        <span style={{ fontFamily: "var(--font-mono)" }}>{mode}</span>. Order doesn't matter — A vs
        B and B vs A aggregate into the same Results page.
      </p>
    </div>
  );
}

function PartialPairEmpty({
  mode,
  set,
  apiName,
}: {
  mode: Mode;
  set: "A" | "B";
  apiName: string;
}) {
  const label = useProfileLabel();
  const other = set === "A" ? "B" : "A";
  const pretty = label(apiName);
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 18,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "5px 12px",
          borderRadius: 999,
          border: "1px solid var(--border)",
          background: "var(--secondary)",
          fontSize: 11,
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.04em",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: "var(--foreground)",
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: set === "A" ? "var(--atf-pink-600)" : "var(--atf-blue-950)",
            }}
          />
          Profile {set}: {pretty}
        </span>
        <span style={{ margin: "0 4px", color: "var(--border)" }}>·</span>
        <span>{mode}</span>
      </div>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}
      >
        Now pick Profile {other} in the sidebar
      </h1>
      <p
        style={{
          margin: 0,
          color: "var(--muted-foreground)",
          fontSize: 13,
          maxWidth: 440,
          textAlign: "center",
          lineHeight: 1.55,
        }}
      >
        Profile {set} is set — <span style={{ fontFamily: "var(--font-mono)" }}>{pretty}</span>. The
        arena needs a second profile to compare against.
      </p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Empty with pair — landing pane showing query box + seeds
// -----------------------------------------------------------------------------

function EmptyWithPair({
  mode,
  profileA,
  profileB,
  query,
  setQuery,
  doSearch,
}: {
  mode: Mode;
  profileA: string;
  profileB: string;
  query: string;
  setQuery: (q: string) => void;
  doSearch: (q: string) => void;
}) {
  const label = useProfileLabel();
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 32,
        overflow: "auto",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 660 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "5px 12px",
            borderRadius: 999,
            border: "1px solid var(--border)",
            background: "var(--secondary)",
            fontSize: 11,
            color: "var(--muted-foreground)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
            marginBottom: 20,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--foreground)",
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: "var(--atf-pink-600)",
              }}
            />
            {label(profileA)}
          </span>
          <span>vs</span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--foreground)",
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: "var(--atf-blue-950)",
              }}
            />
            {label(profileB)}
          </span>
          <span style={{ margin: "0 4px", color: "var(--border)" }}>·</span>
          <span>{mode}</span>
        </div>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            margin: 0,
          }}
        >
          Which response list would you actually use?
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "var(--muted-foreground)",
            marginTop: 12,
            lineHeight: 1.55,
          }}
        >
          Two lists will appear as{" "}
          <b style={{ color: "var(--foreground)", fontWeight: 500 }}>Left</b> and{" "}
          <b style={{ color: "var(--foreground)", fontWeight: 500 }}>Right</b> in random order — you
          won't know which column is A or B until the Results page. Blind vote with four verdicts.
        </p>
      </div>
      <div style={{ width: "100%", maxWidth: 720 }}>
        <QueryBox value={query} onChange={setQuery} onSubmit={() => doSearch(query)} autoFocus />
      </div>
      <div style={{ width: "100%", maxWidth: 720 }}>
        <div className="overline" style={{ marginBottom: 10, textAlign: "center" }}>
          Try a seed query
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {SAMPLE_QUERIES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => {
                setQuery(q);
                doSearch(q);
              }}
              style={{
                padding: "6px 12px",
                border: "1px solid var(--border)",
                borderRadius: 999,
                background: "var(--background)",
                fontSize: 12,
                color: "var(--muted-foreground)",
                cursor: "pointer",
              }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Results view — query box header, columns, vote bar footer
// -----------------------------------------------------------------------------

function ResultsView({
  mode,
  profileA,
  profileB,
  query,
  lastQuery,
  setQuery,
  doSearch,
  phase,
  state,
  verdict,
  castVote,
}: {
  mode: Mode;
  profileA: string;
  profileB: string;
  query: string;
  lastQuery: string;
  setQuery: (q: string) => void;
  doSearch: (q: string) => void;
  phase: Phase;
  state: ResultsState | null;
  verdict: Verdict | null;
  castVote: (v: Verdict) => void;
}) {
  const label = useProfileLabel();
  const inputDisabled = phase === "searching" || phase === "submitted";
  const showColumns = phase === "results" || phase === "submitted";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: "14px 24px",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <QueryBox
            value={query || lastQuery}
            onChange={setQuery}
            onSubmit={() => doSearch(query || lastQuery)}
            disabled={inputDisabled}
          />
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "center",
              gap: 12,
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              color: "var(--muted-foreground)",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: "var(--atf-pink-600)",
                }}
              />
              {label(profileA)}
            </span>
            <span>vs</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: "var(--atf-blue-950)",
                }}
              />
              {label(profileB)}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "16px 24px 0",
        }}
      >
        {phase === "error" && (
          <ErrorBanner onRetry={() => lastQuery.trim() && doSearch(lastQuery)} />
        )}
        {phase === "searching" ? (
          <SearchingSkeleton />
        ) : showColumns && state ? (
          <ResultColumns
            hitsLeft={state.hitsLeft}
            hitsRight={state.hitsRight}
            latencyLeft={state.latencyLeftMs}
            latencyRight={state.latencyRightMs}
            density="comfortable"
            mode={mode}
          />
        ) : null}
      </div>
      <div
        style={{
          padding: "14px 24px 20px",
          borderTop: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <VoteBar onVote={castVote} disabled={inputDisabled || !state} chosen={verdict} />
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: "var(--muted-foreground)",
              textAlign: "center",
              fontFamily: "var(--font-mono)",
            }}
          >
            {phase === "submitted"
              ? "Vote recorded — refreshing for next query…"
              : "Keys 1 · 2 · 3 · 4"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchingSkeleton() {
  return (
    <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
      {[0, 1].map((col) => (
        <div key={col} style={{ flex: 1, display: "grid", gap: 8 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 88,
                border: "1px solid var(--border)",
                borderRadius: 10,
                background:
                  "linear-gradient(90deg, var(--secondary), color-mix(in oklch, var(--secondary) 50%, var(--background)), var(--secondary))",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.4s linear infinite",
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ErrorBanner({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      style={{
        border: "1px solid color-mix(in oklch, var(--destructive) 40%, var(--border))",
        background: "color-mix(in oklch, var(--destructive) 10%, var(--background))",
        borderRadius: 10,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <span
        style={{
          fontSize: 13,
          color: "var(--foreground)",
          flex: 1,
        }}
      >
        Search failed — check your connection and try again.
      </span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: "6px 12px",
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--background)",
          fontSize: 12,
          fontFamily: "var(--font-sans)",
          cursor: "pointer",
          color: "var(--foreground)",
        }}
      >
        Retry
      </button>
    </div>
  );
}
